import i18n from 'i18next';
import {Trans, TransProps, useTranslation} from 'react-i18next';
import {DATE_FNS_LOCALE_MAP, SupportLanguage} from '@di-shared/i18init';
import type {TranslationKey} from '@di-shared/i18next';

export const LANGUAGE_COOKIE_KEY = 'lang';

export function getDateFnsLocale() {
  const language = i18n.resolvedLanguage as SupportLanguage;
  return DATE_FNS_LOCALE_MAP[language];
}

export function getLanguage(): SupportLanguage {
  return i18n.resolvedLanguage as SupportLanguage;
}

export function getIntlLanguage() {
  const language = i18n.resolvedLanguage as SupportLanguage;
  return language.replace(/_/g, '-');
}

export type PlaceHolder = Record<string, unknown>;

export interface InferableTransProps<K extends TranslationKey> {
  translationKey: K;
  placeholders?: PlaceHolder;
  components?: TransProps<TranslationKey>['components'];
}

export interface InferableUseTranslationResponse {
  t: <K extends TranslationKey>(key: K, placeholders?: PlaceHolder) => string;
  language: SupportLanguage;
  InferableTrans: <K extends TranslationKey>(props: InferableTransProps<K>) => JSX.Element;

  setLanguage: (language: SupportLanguage) => void;
  tIfExists: (key: TranslationKey) => string;
}

export function useInferableTranslation(): InferableUseTranslationResponse {
  const {t, i18n} = useTranslation();

  function InferableTrans<K extends TranslationKey>({translationKey, placeholders, components}: InferableTransProps<K>) {
    return Trans({
      i18n,
      i18nKey: translationKey,
      values: placeholders,
      components,
    });
  }

  function tIfExists(key: TranslationKey) {
    if (i18n.exists(key)) {
      return t(key);
    }
    return '';
  }

  return {
    t,
    language: getLanguage(),
    setLanguage: i18n.changeLanguage,
    InferableTrans,
    tIfExists,
  };
}

export const t = i18n.t as InferableUseTranslationResponse['t'];
